import { useMemo } from "react";

/**
 * Resolve the stat field name for a given market key and league.
 * Returns null for unrecognized market/league combinations -- fail closed.
 * @param {string} market - Canonical market key
 * @param {string} league - League identifier (e.g. 'nhl', 'mlb')
 * @returns {string|null}
 */
function resolveStatKey(market, league) {
  switch (league) {
    case 'nhl':
      switch (market) {
        case 'sog':
        case 'nhl:shots_on_goal':
          return 'sog';
        case 'goals':
        case 'nhl:goals':
        case 'anytime_goal':
          return 'goals';
        case 'assists':
        case 'nhl:assists':
        case 'anytime_assist':
        case 'nhl:anytime_assist':
          return 'assists';
        case 'points':
        case 'nhl:points':
        case 'anytime_point':
          return 'points';
        default:
          return null;
      }
    case 'mlb':
      switch (market) {
        case 'mlb:hits':
        case 'hits':
          return 'hits';
        case 'mlb:home_runs':
        case 'home_runs':
          return 'homeRuns';
        case 'mlb:rbis':
        case 'rbis':
          return 'rbi';
        case 'mlb:runs':
        case 'runs':
          return 'runs';
        case 'mlb:total_bases':
        case 'total_bases':
          return 'totalBases';
        case 'mlb:strikeouts_pitcher':
        case 'strikeouts_pitcher':
          return 'pitchingStrikeouts';
        default:
          return null;
      }
    default:
      return null;
  }
}

/**
 * Initialize a player stat object with the correct fields for the given league.
 * @param {string} name - Player name
 * @param {string} league - League identifier
 * @returns {Object}
 */
function initPlayer(name, league) {
  switch (league) {
    case 'nhl':
      return { name, sog: null, goals: null, assists: null, points: null };
    case 'mlb':
      return { name, hits: null, homeRuns: null, rbi: null, runs: null, totalBases: null, pitchingStrikeouts: null };
    default:
      return { name };
  }
}

export function useLiveStats(bets) {
  const liveStats = useMemo(() => {
    const map = {};
    bets.forEach(bet => {
      if (!bet.legs) return;
      bet.legs.forEach(leg => {
        const gid = String(leg.gameId || bet.gameId || '');
        if (!gid) return;
        const val = leg.frozenStat ?? leg.statValue ?? null;
        if (val == null) return;
        const name = leg.player || leg.playerName || leg.pick || '';
        if (!name) return;
        const league = leg.league;
        if (!league) return;
        const market = leg.canonicalMarket || leg.type || leg.market || '';
        const statKey = resolveStatKey(market, league);
        if (!statKey) return;
        if (!map[gid]) map[gid] = [];
        let player = map[gid].find(p => p.name === name);
        if (!player) {
          player = initPlayer(name, league);
          map[gid].push(player);
        }
        player[statKey] = val;
      });
    });
    return map;
  }, [bets]);

  return { liveStats };
}
